import {useCallback, useEffect, useRef, useState} from 'react'
import queryString from 'query-string'
import moment from 'moment'
import {AUTH_PARAMS} from '../../share/urlParams'

/**
 * Đồng bộ bộ lọc của một màn với thanh địa chỉ, để copy URL gửi cho người khác là họ mở ra thấy
 * đúng cái mình đang xem.
 *
 * Cách dùng — khai giá trị mặc định, KIỂU DỮ LIỆU được suy ra từ chính mặc định đó:
 *
 *     const DEFAULTS = {
 *         status: 'missing',        // chuỗi
 *         stageCodes: [],           // mảng   -> ?stageCodes=in,cat
 *         page: 1,                  // số
 *         autoTarget: true,         // luận lý -> ?autoTarget=0
 *         from: moment().startOf('day'),  // moment -> ISO-8601
 *     }
 *
 *     const [filters, setFilter] = useUrlFilters(DEFAULTS, {history, location})
 *     <Select value={filters.status} onChange={(v) => setFilter({status: v})}/>
 *
 * Ba điều đáng biết:
 *
 * 1. CHỈ ghi lên URL những khoá KHÁC mặc định. Bộ lọc để nguyên thì URL sạch trơn, người nhận link
 *    không phải đoán xem cái nào là do người gửi cố ý chọn.
 *
 * 2. Mặc định dùng `history.replace`, không phải `push`. Gõ vào ô lọc mà mỗi ký tự đẻ một mục
 *    lịch sử thì nút Back của trình duyệt thành vô dụng. Cần Back đi ngược từng bước thì
 *    truyền `push: true`.
 *
 * 3. Query nào không nằm trong `defaults` được GIỮ NGUYÊN — hook chỉ quản đúng phần nó biết, không
 *    xoá tham số của người khác.
 *
 * ⚠️ `defaults` phải ổn định giữa các lần render (khai ngoài component, hoặc bọc `useMemo`). Hook
 *    chốt lấy tham chiếu đầu tiên; khai inline thì mỗi render là một object mới và effect chạy loạn.
 */

const isMoment = (value) => moment.isMoment(value)

/** Suy kiểu từ giá trị mặc định — không bắt người dùng khai schema riêng. */
const kindOf = (value) => {
    if (Array.isArray(value)) return 'array'
    if (typeof value === 'boolean') return 'boolean'
    if (typeof value === 'number') return 'number'
    if (isMoment(value)) return 'date'

    return 'string'
}

const decode = (raw, fallback) => {
    switch (kindOf(fallback)) {
        case 'array':
            return `${raw}`.split(',').map((item) => item.trim()).filter(Boolean)
        case 'boolean':
            return raw === '1' || raw === 'true'
        case 'number': {
            const parsed = Number(raw)

            return Number.isFinite(parsed) ? parsed : fallback
        }
        case 'date': {
            const parsed = moment(raw, moment.ISO_8601)

            return parsed.isValid() ? parsed : fallback
        }
        default:
            return `${raw}`
    }
}

const encode = (value) => {
    if (Array.isArray(value)) return value.join(',')
    if (typeof value === 'boolean') return value ? '1' : '0'
    if (isMoment(value)) return value.toISOString()

    return `${value}`
}

/** So sánh theo kiểu — mảng và moment không so được bằng `===`. */
const isSame = (a, b) => {
    if (Array.isArray(a) || Array.isArray(b)) {
        const left = Array.isArray(a) ? a : []
        const right = Array.isArray(b) ? b : []

        return left.length === right.length && left.every((item, i) => item === right[i])
    }

    if (isMoment(a) || isMoment(b)) {
        if (!isMoment(a) || !isMoment(b)) return false

        return a.isSame(b)
    }

    return a === b
}

/** Đọc bộ lọc từ query. Khoá thiếu hoặc rỗng thì lấy mặc định. */
export const parseUrlFilters = (search, defaults) => {
    const query = queryString.parse(search || '')

    return Object.keys(defaults).reduce((acc, key) => {
        const raw = query[key]

        acc[key] = raw === undefined || raw === null || raw === ''
            ? defaults[key]
            : decode(Array.isArray(raw) ? raw[0] : raw, defaults[key])

        return acc
    }, {})
}

/** Dựng lại chuỗi query: giữ tham số lạ, chỉ ghi khoá khác mặc định. */
/**
 * Tham số xác thực KHÔNG bao giờ được ghi lại lên URL.
 *
 * `WithAuthentication` đọc `?token=…&refresh=…` rồi dọn URL bằng `window.history.replaceState`.
 * Lệnh đó thao tác thẳng lên History API nên **react-router không hề biết** — `location.search`
 * trong bộ nhớ router vẫn còn nguyên hai tham số đó. Hook này đọc từ router, và `buildUrlSearch`
 * cố ý giữ lại mọi khoá lạ (để không nuốt query của màn khác), nên nó sẽ bơm token NGƯỢC lên thanh
 * địa chỉ. `queryString.stringify` còn sắp lại thứ tự khoá nên guard `search === location.search`
 * cũng không chặn được.
 *
 * Hậu quả nếu không lọc: JWT hiện nguyên văn trên thanh địa chỉ của 4 màn dùng hook này, đi vào
 * header `Referer` của mọi request ngoài, và dính vào chính cái link mà hook này khuyến khích người
 * dùng copy gửi cho nhau — chia sẻ bộ lọc hoá ra chia sẻ luôn phiên đăng nhập.
 */
export const buildUrlSearch = (values, defaults, search) => {
    const query = queryString.parse(search || '')

    AUTH_PARAMS.forEach((key) => delete query[key])

    Object.keys(defaults).forEach((key) => {
        const value = values[key]
        const isEmptyArray = Array.isArray(value) && !value.length

        if (isSame(value, defaults[key]) || value === null || value === undefined || value === '' || isEmptyArray) {
            delete query[key]

            return
        }

        query[key] = encode(value)
    })

    const next = queryString.stringify(query)

    return next ? `?${next}` : ''
}

/**
 * Chỉ GHI bộ lọc lên URL, state do màn tự giữ.
 *
 * Dùng cho màn đã có sẵn nhiều `useState` mà không muốn viết lại — chỉ cần gom giá trị hiện tại vào
 * một object rồi truyền vào đây.
 */
export const useUrlFilterSync = (values, defaults, {history, location, push = false} = {}) => {
    const defaultsRef = useRef(defaults)
    const search = buildUrlSearch(values, defaultsRef.current, location.search)

    useEffect(() => {
        if (search === location.search) return

        const to = {pathname: location.pathname, search}

        if (push) {
            history.push(to)
        } else {
            history.replace(to)
        }
        /*
         * ⚠️ Hook này đồng bộ MỘT CHIỀU: state → URL. `values` là `useState` chốt lúc mount và
         *    không có đường đồng bộ ngược, nên bấm Back đổi `location.search` mà `values` giữ
         *    nguyên → `buildUrlSearch` dựng lại giá trị CŨ và ghi đè lên. `location.search` nằm
         *    trong deps chỉ để effect chạy lại sau mỗi lần điều hướng, KHÔNG phải để chặn ghi đè.
         *
         *    Màn nào cần Back có tác dụng (ví dụ Back để đóng modal) thì đừng dùng hook này — đọc
         *    thẳng `location` và ghi thẳng `history`, xem `standard-time/StageListPage.js`.
         */
    }, [search, location.search, location.pathname, history, push])
}

/**
 * Bộ lọc dùng URL làm nơi lưu duy nhất.
 *
 * Trả về `[filters, setFilter, resetFilters, isDirty]`. `setFilter` nhận object vá từng phần
 * (`setFilter({page: 2})`), không phải thay cả cụm.
 *
 * `isDirty` = có ít nhất một ô khác mặc định. Dùng để hiện nút "Đặt lại bộ lọc" đúng lúc: bộ lọc
 * nằm trên URL nên nó sống qua refresh và nút Back, người dùng cần một đường về nhanh thay vì phải
 * gạt từng ô một.
 */
export default function useUrlFilters(defaults, {history, location, push = false} = {}) {
    const defaultsRef = useRef(defaults)
    const [values, setValues] = useState(() => parseUrlFilters(location.search, defaultsRef.current))

    useUrlFilterSync(values, defaultsRef.current, {history, location, push})

    const setFilter = useCallback((patch) => {
        setValues((prev) => ({...prev, ...patch}))
    }, [])

    const resetFilters = useCallback(() => {
        setValues(defaultsRef.current)
    }, [])

    // Dựng lại chuỗi query trên nền RỖNG: khác mặc định thì mới có gì để ghi. Dùng lại đúng hàm
    // so sánh của phần ghi URL, khỏi viết thêm một bộ so sánh thứ hai rồi hai bên lệch nhau.
    const isDirty = buildUrlSearch(values, defaultsRef.current, '') !== ''

    return [values, setFilter, resetFilters, isDirty]
}
